import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import { isInside } from "../../../../utils";

/*
Constants and helpers
*/

const INPUT_MAX_LENGTH = 50;
const INPUT_PLACEHOLDER = "";

const validateInput = (value, re) => {
  if (!re) {
    return true;
  }

  return !!String(value).match(re);
};

/*
Component
*/

const BluInputElt = (props) => {
  const {
    inputValue,
    maxLength,
    placeholder,
    regexComplete,
    regexPartial,
    updateValue,
  } = props;

  const [currentPlaceholder, setCurrentPlaceholder] = useState("");
  const [currentValue, setCurrentValue] = useState("");
  const [touched, setTouched] = useState(false);

  const inputElt = useRef();

  useEffect(() => {
    setCurrentPlaceholder(placeholder);
  }, [placeholder]);

  useEffect(() => {
    setCurrentValue(inputValue || "");
  }, [inputValue]);

  const updateOld = (value) => {
    updateValue(value);
  };

  const updateFilter = () => {
    // If untouched - return
    if (!touched) {
      return;
    }

    // Set touched flag
    setTouched(false);

    // If the filter has not changed - return
    if (currentValue === inputValue) {
      return;
    }

    // Validate
    const validated = validateInput(currentValue, regexComplete);
    if (!validated) {
      return;
    }

    // Update or clear the filter
    updateOld(currentValue || null);
  };

  // Submit page number on outside click
  useEffect(() => {
    const onOutsideClick = (evt) => {
      const inside = isInside(evt, inputElt.current);

      // If inside the input element - return
      if (inside) {
        return;
      }

      // Update inputValue to currentValue
      updateFilter();
    };

    window.addEventListener("click", onOutsideClick, false);

    return () => window.removeEventListener("click", onOutsideClick, false);
  });

  // Restore on 'Escape', submit on "Enter",
  const onKeyDownInput = (evt) => {
    const { key, target: eventTarget } = evt;

    // If key is 'Escape', restore currentValue (to inputValue)
    if (key === "Escape") {
      // Blur
      eventTarget.blur();

      setCurrentValue(inputValue || "");

      return;
    }

    // If key is 'Enter', update inputValue (to currentValue)
    if (key === "Enter") {
      // Blur
      eventTarget.blur();

      updateFilter();
    }
  };

  const onChangeInput = (evt) => {
    const { target = {} } = evt;
    const { value = "" } = target;

    const validated = validateInput(value, regexPartial);

    if (!validated) {
      return;
    }

    setCurrentValue(value);
    setTouched(true);
  };

  const onFocusInput = () => {
    setCurrentPlaceholder("");
  };

  const onBlurInput = () => {
    setCurrentPlaceholder(placeholder);
  };

  return (
    <input
      maxLength={maxLength}
      onBlur={onBlurInput}
      onChange={onChangeInput}
      onFocus={onFocusInput}
      onKeyDown={onKeyDownInput}
      placeholder={currentPlaceholder}
      ref={inputElt}
      type="text"
      value={currentValue}
    />
  );
};

BluInputElt.propTypes = {
  inputValue: PropTypes.string,
  maxLength: PropTypes.number,
  placeholder: PropTypes.string,
  regexComplete: PropTypes.instanceOf(RegExp),
  regexPartial: PropTypes.instanceOf(RegExp),
  updateValue: PropTypes.func,
};

BluInputElt.defaultProps = {
  inputValue: "",
  maxLength: INPUT_MAX_LENGTH,
  placeholder: INPUT_PLACEHOLDER,
  regexComplete: "",
  regexPartial: "",
  updateValue: () => {},
};

export default BluInputElt;
